import { isEmbeddedWebview } from "./appUpdate";
/**
 * Слой обращения к нашему /api/v2/*.
 *
 * Зачем модуль:
 *   - Один источник правды для URL'ов: при смене бэка меняем здесь, не по всем компонентам.
 *   - JWT подставляется автоматически из localStorage.
 *   - 401 от сервера → разлогин и редирект на форму входа (через storage event).
 *   - 501 (ФЛ ИНН в разработке) — типовая ошибка, прокидывается с понятным сообщением.
 */
import type {
  AllMethodologiesResult, Methodology, MethodologyDefinition,
  MethodologyVariable, MethodologyFunction, MethodologyTemplate, CustomMethodologyResult,
  Bank, BrokerDict, BrokerCalcResult, ExperienceItem, RelatedCompanyShort,
  BankCatalogItem, LikelyParticipant, SuggestedTender,
} from "../types";

const TOKEN_KEY = "anyskin_token";
const USER_KEY = "anyskin_user";

export interface CurrentUser {
  id: number;
  username: string;
  email: string | null;
  // Профиль физлица: регистрация теперь личная, компания заводится в кабинете (#385).
  full_name?: string | null;
  phone?: string | null;
  is_admin: boolean;
  is_premium?: boolean;
  email_verified: boolean;
  allowed_sources?: string[];   // доступные источники данных банков (раздел «Брокер»)
  org_id?: number | null;       // организация пользователя (надстройка)
  org_role?: "owner" | "admin" | "member" | "platform_operator" | null;  // роль в организации
}

// ── Организация (надстройка) ─────────────────────────────────────────────────
export interface Organization {
  id: number;
  inn: string | null;
  kind: string | null;
  full_name: string | null;
  ogrn: string | null;
  opf: string | null;
  region: string | null;
  status: string | null;
  card_status: string | null;
  card: Record<string, any>;    // финданные/реквизиты (kpp/director_*/phone/bank)
  is_premium: boolean;
  packages: string[];
  allowed_sources: string[];
  allowed_bank_ids?: number[] | null;   // белый список глобальных банков; null — все
  created_at: string | null;
  updated_at: string | null;
}

export interface OrgMember {
  id: number;
  username: string;
  email: string | null;
  org_role: "owner" | "admin" | "member";
  is_active: boolean;
  email_verified: boolean;
}

export interface OrgApiKey {
  id: number;
  prefix: string;
  scopes: string;
  created_at: string | null;
  last_used_at: string | null;
  expires_at: string | null;
}

// Сессия виджета внутри карточки Битрикса (#654) живёт только в памяти вкладки.
// Причин две. Во-первых, в стороннем фрейме localStorage либо разделён по
// верхнему сайту, либо (Safari) недоступен вовсе — обращение к нему бросает
// SecurityError, и виджет просто не открылся бы. Во-вторых, так и правильнее:
// токен умирает вместе с фреймом, а не лежит в хранилище чужого сайта.
let memoryToken: string | null = null;

export function setMemoryToken(token: string | null) {
  memoryToken = token;
}

export function getToken(): string | null {
  if (memoryToken) return memoryToken;
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    // Приватное окно, запрет на хранилище сайта, сторонний фрейм — не повод падать.
    return null;
  }
}

export function getUser(): CurrentUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setSession(token: string, user: CurrentUser) {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  // Уведомить компоненты в этой же вкладке (по умолчанию storage-event не стреляет).
  window.dispatchEvent(new Event("anyskin-auth"));
}

export function clearSession() {
  memoryToken = null;
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {
    // Хранилище недоступно (сторонний фрейм, приватное окно) — чистить нечего.
  }
  window.dispatchEvent(new Event("anyskin-auth"));
}

/**
 * Унифицированный JSON-запрос с автоматическим Bearer-токеном и понятными ошибками.
 * Бросает Error с сообщением, которое уже можно показывать пользователю.
 */
/** Откуда открыто приложение: tg / max — мини-приложение мессенджера, wv — другой встроенный
 *  браузер, pwa — установленное приложение, web — обычный браузер. */
export function clientKind(): "web" | "pwa" | "tg" | "max" | "wv" {
  const w = window as any;
  const ua = navigator.userAgent || "";
  if (w.Telegram?.WebApp?.initData || w.TelegramWebviewProxy || /tgWebApp/i.test(location.hash) || /Telegram/i.test(ua)) return "tg";
  if (w.WebApp?.initData || /MAX\/|MaxMessenger|OneMe/i.test(ua)) return "max";
  if (isEmbeddedWebview()) return "wv";
  if (window.matchMedia("(display-mode: standalone)").matches || (navigator as any).standalone) return "pwa";
  return "web";
}

export async function apiFetch<T = any>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const token = getToken();
  const headers = new Headers(init.headers || {});
  if (!headers.has("Content-Type") && init.body) {
    headers.set("Content-Type", "application/json");
  }
  if (token && !path.startsWith("/api/auth/login") && !path.startsWith("/api/auth/register")) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  // Метрики мобильной версии (M-70): клиент (web/pwa/tg/max/wv) и узкий ли экран.
  headers.set("X-Client", clientKind());
  headers.set("X-Client-Width", window.matchMedia("(max-width: 767.98px)").matches ? "m" : "d");

  let resp: Response;
  try {
    resp = await fetch(path, { ...init, headers });
  } catch (e: any) {
    throw new Error("Сеть недоступна: " + (e?.message ?? e));
  }

  // 401 на login/register — это «неверный логин или пароль», а не истёкшая сессия:
  // настоящую причину покажет блок !resp.ok ниже (detail с бэка). Сессию не чистим.
  if (resp.status === 401
      && !path.startsWith("/api/auth/login")
      && !path.startsWith("/api/auth/register")) {
    clearSession();
    throw new Error("Сессия истекла, войдите снова.");
  }

  let body: any = null;
  try {
    body = await resp.json();
  } catch {
    /* пустой ответ — окей */
  }

  // 402 — достигнут лимит бесплатного доступа: типизированная ошибка для апселла.
  if (resp.status === 402) {
    const info = (body && body.detail) || {};
    const e = new Error(info.message || "Доступно в премиум-доступе");
    (e as any).code = "inn_limit";
    (e as any).info = info;
    throw e;
  }

  if (!resp.ok) {
    const detail = body && (body.detail || body.error);
    if (Array.isArray(detail)) {
      throw new Error(detail.map((d: any) => d.msg || d).join("; "));
    }
    // detail-объект ({message, code, …} — напр. отказ СНС): текст — в message, сам объект —
    // в e.detail, чтобы вызывающий мог ветвиться по коду.
    if (detail && typeof detail === "object") {
      // requestId СНС просят при обращении в их поддержку — показываем его рядом с текстом,
      // иначе брокеру нечего сообщить, а в логах его ищут вручную.
      const ref = [detail.requestId && `запрос ${detail.requestId}`,
                   detail.traceId && `трассировка ${detail.traceId}`].filter(Boolean).join(", ");
      const e = new Error((detail.message || `HTTP ${resp.status}`) + (ref ? ` (${ref})` : ""));
      (e as any).detail = detail; (e as any).status = resp.status;
      throw e;
    }
    throw new Error(detail || `HTTP ${resp.status}`);
  }

  return body as T;
}

// ── Version (публичный эндпоинт) ─────────────────────────────────────────────

export interface AppVersion { name: string; version: string; git_sha?: string }

export async function fetchVersion(): Promise<AppVersion> {
  return apiFetch<AppVersion>("/api/version");
}

// ── Auth ─────────────────────────────────────────────────────────────────────

export interface WidgetSession {
  token: string;
  view: string | null;
  ref: string | null;
  bankId: string | number | null;
  user: { id: number; username: string | null; fullName: string | null; orgRole: string | null };
}

/** Обмен одноразового тикета виджета на сессию (#654). Без Authorization — тикет и
 *  есть доказательство: он подписан ключом ядра, живёт 60 секунд и гасится при
 *  первом предъявлении. */
export async function exchangeWidgetTicket(ticket: string): Promise<WidgetSession> {
  return apiFetch<WidgetSession>("/api/integration/sso/widget/exchange", {
    method: "POST",
    body: JSON.stringify({ ticket }),
  });
}

// ── Эмуляция вызова виджета из Битрикса — кнопка в админке (#1047) ───────────
// Витрину нельзя открыть «просто так»: вход в неё — одноразовый тикет, который
// выписывает сервер Битрикса своим постоянным ключом. Пока портала нет, админ
// выписывает ТОТ ЖЕ тикет сам и смотрит ТУ ЖЕ витрину.

export interface WidgetPreview {
  url: string;
  expiresIn: number;
  user: { id: number; email: string | null; fullName: string | null; orgRole: string | null };
  /** Запрос, которым портал получит этот же адрес — показываем рядом для интеграторов. */
  bitrix: { method: string; path: string; headers: Record<string, string>; body: Record<string, any> };
  case?: {
    ref: string; status: string | null; inn: string | null;
    companyName: string | null; orgId: number | null; banks: number;
  };
}

export interface WidgetCaseRow {
  ref: string;
  status: string | null;
  inn: string | null;
  companyName: string | null;
  orgId: number | null;
  ownerId: number | null;
  createdAt: string | null;
  banks: number;
}

export async function adminWidgetPreview(req: {
  view: string; ref?: string | null; bankId?: string | null; email?: string | null;
}): Promise<WidgetPreview> {
  return apiFetch<WidgetPreview>("/api/admin/widget/preview", {
    method: "POST",
    body: JSON.stringify({
      view: req.view,
      ref: req.ref?.trim() || null,
      bankId: req.bankId?.trim() || null,
      email: req.email?.trim() || null,
    }),
  });
}

export async function adminWidgetCases(limit = 20): Promise<WidgetCaseRow[]> {
  const out = await apiFetch<{ items: WidgetCaseRow[] }>(`/api/admin/widget/cases?limit=${limit}`);
  return out.items || [];
}

export async function whoami(): Promise<CurrentUser> {
  return apiFetch<CurrentUser>("/api/auth/me");
}

// SSO с тендераналитикой (Фаза 6 плана выноса в суб-продукт) — одноразовый
// короткоживущий (60с) кросс-доменный тикет вместо iframe+postMessage (#783).
export async function getTenderAnalyticsSsoRedirect(): Promise<string> {
  const data = await apiFetch<{ redirect_url: string }>("/api/integration/sso/ta-ticket", { method: "POST" });
  return data.redirect_url;
}

export async function login(username: string, password: string): Promise<{ token: string; user: CurrentUser }> {
  const data = await apiFetch<{ access_token: string; user: CurrentUser }>(
    "/api/auth/login",
    { method: "POST", body: JSON.stringify({ username, password }) },
  );
  setSession(data.access_token, data.user);
  return { token: data.access_token, user: data.user };
}

export interface RegisterOrg {
  inn?: string;
  company_name?: string;
  kpp?: string;
  director_fio?: string;
  director_position?: string;
  phone?: string;
  bank_bik?: string;
  bank_name?: string;
  bank_account?: string;
}

// ── Согласия пользователя (#387) ─────────────────────────────────────────────
export type ConsentKind = "license" | "pdn" | "third_party" | "ads";
export interface LegalDoc { kind: ConsentKind; slug: string; title: string; required: boolean }
export interface ConsentState {
  accepted: boolean | null; at: string | null; required: boolean; title: string; slug: string;
}

/** Список соглашений — публичный: их читают ДО регистрации. */
export async function listLegalDocuments(): Promise<LegalDoc[]> {
  const r = await fetch("/api/legal");
  if (!r.ok) throw new Error("Не удалось загрузить список соглашений");
  return (await r.json()).documents as LegalDoc[];
}

/** Текст одного соглашения (тоже публично). */
export async function fetchLegalDocument(slug: string): Promise<{ title: string; body: string; updatedAt: string | null }> {
  const r = await fetch(`/api/legal/${encodeURIComponent(slug)}`);
  if (!r.ok) throw new Error("Текст соглашения недоступен");
  return r.json();
}

export async function fetchMyConsents(): Promise<Record<ConsentKind, ConsentState>> {
  return (await apiFetch<{ consents: Record<ConsentKind, ConsentState> }>("/api/auth/consents")).consents;
}

export async function setMyConsent(kind: ConsentKind, accepted: boolean): Promise<Record<ConsentKind, ConsentState>> {
  const r = await apiFetch<{ consents: Record<ConsentKind, ConsentState> }>("/api/auth/consents", {
    method: "PUT", body: JSON.stringify({ kind, accepted }),
  });
  return r.consents;
}

/** Регистрация физлица: ФИО, email, телефон, пароль. Логин не спрашиваем — бэкенд
 *  выведет его из email; компания заводится отдельно, в личном кабинете (#385).
 *  `org` оставлен для обратной совместимости вызовов (форма его больше не шлёт). */
export async function register(
  person: { full_name: string; email: string; password: string; phone?: string;
            consents?: Partial<Record<ConsentKind, boolean>> },
  org?: RegisterOrg,
): Promise<{ status: string; email: string; username: string; message: string }> {
  // Пустые строки не шлём — карточка организации хранит только заполненное.
  const orgClean: Record<string, string> = {};
  for (const [k, v] of Object.entries(org || {})) {
    if (v && String(v).trim()) orgClean[k] = String(v).trim();
  }
  return apiFetch("/api/auth/register", {
    method: "POST",
    body: JSON.stringify({
      full_name: person.full_name.trim(),
      email: person.email.trim(),
      phone: (person.phone || "").trim() || undefined,
      password: person.password,
      consents: person.consents || {},
      ...orgClean,
    }),
  });
}

/** Правка своего профиля (ФИО/телефон) — личный кабинет. */
export async function updateProfile(patch: { full_name?: string; phone?: string }): Promise<CurrentUser> {
  return apiFetch<CurrentUser>("/api/auth/me", { method: "PUT", body: JSON.stringify(patch) });
}

// ── Организация (кабинет: карточка / участники / API-ключ) ───────────────────
export async function getOrg(): Promise<{ organization: Organization; role: string | null }> {
  return apiFetch("/api/org");
}
export async function updateOrgCard(patch: Record<string, any>): Promise<Organization> {
  return apiFetch("/api/org/card", { method: "PUT", body: JSON.stringify(patch) });
}
export async function autofillOrgCard(inn: string): Promise<Organization> {
  return apiFetch("/api/org/card/autofill", { method: "POST", body: JSON.stringify({ inn }) });
}
export async function listOrgMembers(): Promise<OrgMember[]> {
  return apiFetch("/api/org/members");
}
export async function addOrgMember(m: { username: string; email: string; password?: string; role?: string }): Promise<OrgMember & { inviteSent?: boolean }> {
  return apiFetch("/api/org/members", { method: "POST", body: JSON.stringify(m) });
}
export async function resendOrgInvite(memberId: number): Promise<{ status: string }> {
  return apiFetch(`/api/org/members/${memberId}/resend-invite`, { method: "POST" });
}
export async function attachOrgMember(email: string, role = "member"): Promise<{ status: string; id: number; username: string; org_role: string }> {
  return apiFetch("/api/org/members/attach", { method: "POST", body: JSON.stringify({ email, role }) });
}
export async function setOrgMemberRole(memberId: number, role: string): Promise<{ status: string }> {
  return apiFetch(`/api/org/members/${memberId}/role`, { method: "PUT", body: JSON.stringify({ role }) });
}
export async function removeOrgMember(memberId: number): Promise<{ status: string }> {
  return apiFetch(`/api/org/members/${memberId}`, { method: "DELETE" });
}
export async function getOrgApiKey(): Promise<{ key: OrgApiKey | null }> {
  return apiFetch("/api/org/api-key");
}
export async function rotateOrgApiKey(scopes?: string[]): Promise<{ id: number; prefix: string; scopes: string; token: string }> {
  return apiFetch("/api/org/api-key", { method: "POST", body: JSON.stringify(scopes ? { scopes } : {}) });
}
export async function revokeOrgApiKey(): Promise<{ status: string; revoked: number }> {
  return apiFetch("/api/org/api-key", { method: "DELETE" });
}
/** Подборы ВСЕЙ организации — доступно владельцу и админу организации.
 *  В каждой записи `by` — сотрудник, который её сделал. */
export async function listOrgSelections(inn?: string, status?: string, limit = 100):
  Promise<(BrokerSelection & { by?: string | null })[]> {
  const q = new URLSearchParams();
  if (inn) q.set("inn", inn);
  if (status) q.set("status", status);
  q.set("limit", String(limit));
  return apiFetch(`/api/org/selections?${q.toString()}`);
}

export async function resendConfirmation(username: string): Promise<void> {
  await apiFetch("/api/auth/resend", {
    method: "POST",
    body: JSON.stringify({ username }),
  });
}

// ── Приглашение сотрудника: установка пароля по ссылке из письма ─────────────
export async function inviteInfo(token: string): Promise<{ username: string; email: string | null }> {
  return apiFetch(`/api/auth/invite/${encodeURIComponent(token)}`);
}
export async function inviteSetPassword(token: string, password: string): Promise<{ status: string }> {
  return apiFetch(`/api/auth/invite/${encodeURIComponent(token)}`, {
    method: "POST",
    body: JSON.stringify({ password }),
  });
}

// ── Methodologies (/api/v2) ──────────────────────────────────────────────────

export async function calculateAll(
  inn: string,
  desiredLimit: number,
): Promise<AllMethodologiesResult> {
  return apiFetch<AllMethodologiesResult>("/api/v2/calculate", {
    method: "POST",
    body: JSON.stringify({ inn, desiredLimit }),
  });
}

export async function compareInn(inn: string): Promise<AllMethodologiesResult> {
  return apiFetch<AllMethodologiesResult>(`/api/v2/compare/${inn}`);
}

// Расчёт всех методик по ЗАГРУЖЕННОЙ пользователем отчётности (multipart).
export async function calculateWithReport(
  inn: string, desiredLimit: number, file: File,
): Promise<AllMethodologiesResult> {
  const fd = new FormData();
  fd.append("inn", inn);
  fd.append("desiredLimit", String(desiredLimit));
  fd.append("file", file);
  const token = getToken();
  const resp = await fetch("/api/v2/calculate-with-report", {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  if (resp.status === 401) { clearSession(); throw new Error("Сессия истекла, войдите снова."); }
  const body = await resp.json().catch(() => null);
  if (resp.status === 402) {
    const info = (body && body.detail) || {};
    const e = new Error(info.message || "Доступно в премиум-доступе");
    (e as any).code = "inn_limit"; (e as any).info = info;
    throw e;
  }
  if (!resp.ok) {
    const detail = body && (body.detail || body.error);
    // Пустой 502/504 приходит не от приложения, а от прокси, когда распознавание шло
    // дольше его срока. Документ при этом НЕ потерян: сервис дораспознает его, и строки
    // лягут в отчётность клиента (#585). «HTTP 504» оператору об этом не говорит.
    if (!detail && (resp.status === 504 || resp.status === 502))
      throw new Error("Распознавание заняло дольше обычного — сервис мог быть занят другим "
        + "документом. Отчётность клиента дозаполнится сама; повторите расчёт через минуту.");
    throw new Error(Array.isArray(detail) ? detail.map((d: any) => d.msg || d).join("; ") : (detail || `HTTP ${resp.status}`));
  }
  return body as AllMethodologiesResult;
}

export interface HistoryRow {
  inn: string;
  rating: string;
  total: number | string;
  date: string;
  g1?: number | string;
  g2?: number | string;
  usl?: number | string;
}

export async function fetchHistory(limit = 50): Promise<HistoryRow[]> {
  return apiFetch<HistoryRow[]>(`/api/v2/history?limit=${limit}`);
}

// ── Admin ────────────────────────────────────────────────────────────────────

export interface AdminUserRow {
  id: number;
  username: string;
  email: string | null;
  is_active: boolean;
  is_admin: boolean;
  is_premium: boolean;
  allowed_sources?: string[];
  packages?: string[];
  email_verified: boolean;
  created_at: string | null;
  last_login_at: string | null;
  org_id?: number | null;
  org_role?: string | null;
  org_name?: string | null;
}

export async function adminListUsers(): Promise<AdminUserRow[]> {
  return apiFetch("/api/admin/users");
}

// ── Организации (супер-админ) ────────────────────────────────────────────────
export interface AdminOrg {
  id: number;
  full_name: string | null;
  inn: string | null;
  is_premium: boolean;
  packages: string[];
  allowed_sources: string[];
  allowed_bank_ids: number[] | null;   // белый список глобальных банков; null — все
  created_at: string | null;
  member_count: number;
  owner_username: string | null;
}
export interface GlobalBank { id: number; name: string; is_active: boolean; }
export async function adminListOrgs(): Promise<AdminOrg[]> {
  return apiFetch("/api/admin/orgs");
}
export async function adminCreateOrg(payload: {
  full_name: string; inn?: string; is_premium?: boolean; packages?: string[]; owner_user_id?: number;
}): Promise<Organization> {
  return apiFetch("/api/admin/orgs", { method: "POST", body: JSON.stringify(payload) });
}
export async function adminGetOrg(orgId: number): Promise<{ organization: Organization; members: OrgMember[] }> {
  return apiFetch(`/api/admin/orgs/${orgId}`);
}
export async function adminAttachOrgMember(orgId: number, m: { user_id?: number; email?: string; role?: string }): Promise<{ status: string; id: number; org_role: string }> {
  return apiFetch(`/api/admin/orgs/${orgId}/members`, { method: "POST", body: JSON.stringify(m) });
}
export async function adminSetOrgMemberRole(orgId: number, userId: number, role: string): Promise<{ status: string }> {
  return apiFetch(`/api/admin/orgs/${orgId}/members/${userId}/role`, { method: "PUT", body: JSON.stringify({ role }) });
}
export async function adminDetachOrgMember(orgId: number, userId: number): Promise<{ status: string; new_org_id: number }> {
  return apiFetch(`/api/admin/orgs/${orgId}/members/${userId}`, { method: "DELETE" });
}

// Супер-админ: доступ организации (эпик #270) — источники / пакеты / белый список банков.
export async function adminGlobalBanks(): Promise<GlobalBank[]> {
  return apiFetch("/api/admin/global-banks");
}
export async function adminSetOrgSources(orgId: number, allowed_sources: string[]): Promise<{ allowed_sources: string[] }> {
  return apiFetch(`/api/admin/orgs/${orgId}/sources`, { method: "PUT", body: JSON.stringify({ allowed_sources }) });
}
export async function adminSetOrgPackages(orgId: number, packages: string[]): Promise<{ packages: string[] }> {
  return apiFetch(`/api/admin/orgs/${orgId}/packages`, { method: "PUT", body: JSON.stringify({ packages }) });
}
export async function adminSetOrgBanks(orgId: number, allowed_bank_ids: number[] | null): Promise<{ allowed_bank_ids: number[] | null }> {
  return apiFetch(`/api/admin/orgs/${orgId}/banks`, { method: "PUT", body: JSON.stringify({ allowed_bank_ids }) });
}

// Мультиорганизационность (эпик #278): членства пользователя + переключение кабинета.
export interface MyOrg { id: number; full_name: string | null; inn: string | null; org_role: string; active: boolean; }
export async function orgMine(): Promise<MyOrg[]> {
  return apiFetch("/api/org/mine");
}
export async function orgSwitch(orgId: number): Promise<{ status: string; active_org_id: number }> {
  return apiFetch("/api/org/switch", { method: "POST", body: JSON.stringify({ org_id: orgId }) });
}
export async function adminAddOrgMembership(orgId: number, m: { user_id?: number; email?: string; role?: string }): Promise<{ status: string; id: number; org_role: string }> {
  return apiFetch(`/api/admin/orgs/${orgId}/memberships`, { method: "POST", body: JSON.stringify(m) });
}

export interface UserUsage { userId: number; today: number; month: number; }

export async function adminUsersUsage(): Promise<UserUsage[]> {
  return apiFetch("/api/admin/users/usage");
}

export async function adminCreateUser(payload: {
  username: string; email: string; password: string; is_admin?: boolean; is_premium?: boolean;
  allowed_sources?: string[]; packages?: string[];
}): Promise<AdminUserRow> {
  return apiFetch("/api/admin/users", { method: "POST", body: JSON.stringify(payload) });
}

export async function adminSetUserSources(userId: number, sources: string[]): Promise<{ allowed_sources: string[] }> {
  return apiFetch(`/api/admin/users/${userId}/sources`, {
    method: "PUT", body: JSON.stringify({ sources }),
  });
}

export async function adminSetUserPackages(userId: number, packages: string[]): Promise<{ packages: string[] }> {
  return apiFetch(`/api/admin/users/${userId}/packages`, {
    method: "PUT", body: JSON.stringify({ packages }),
  });
}

export async function adminVerify(userId: number): Promise<void> {
  await apiFetch(`/api/admin/users/${userId}/verify`, { method: "POST" });
}

export async function adminBlock(userId: number): Promise<void> {
  await apiFetch(`/api/admin/users/${userId}/block`, { method: "POST" });
}

export async function adminUnblock(userId: number): Promise<void> {
  await apiFetch(`/api/admin/users/${userId}/unblock`, { method: "POST" });
}

export async function adminDelete(userId: number): Promise<void> {
  await apiFetch(`/api/admin/users/${userId}`, { method: "DELETE" });
}

export async function adminSetPassword(userId: number, password: string): Promise<void> {
  await apiFetch(`/api/admin/users/${userId}/set-password`, {
    method: "POST", body: JSON.stringify({ password }),
  });
}

export async function adminPromote(userId: number): Promise<void> {
  await apiFetch(`/api/admin/users/${userId}/promote`, { method: "POST" });
}

export async function adminDemote(userId: number): Promise<void> {
  await apiFetch(`/api/admin/users/${userId}/demote`, { method: "POST" });
}

export async function adminGrantPremium(userId: number): Promise<void> {
  await apiFetch(`/api/admin/users/${userId}/grant-premium`, { method: "POST" });
}

export async function adminRevokePremium(userId: number): Promise<void> {
  await apiFetch(`/api/admin/users/${userId}/revoke-premium`, { method: "POST" });
}

// ── Банковские методики (админ) ──────────────────────────────────────────────
export interface BankMethodIndicator {
  code: string; label: string; unit: string; formula: string;
  implemented: boolean;
  override: { formula: string; enabled: boolean; label: string } | null;
}
export interface BankMethodBlock {
  code: string; title: string; aggregate: string; indicators: BankMethodIndicator[];
}
export interface BankMethodAdmin {
  key: string; anonName: string; bank: string; product: string; description: string;
  amountMin: number | null; amountMax: number | null; active: boolean; blocks: BankMethodBlock[];
}

export async function adminBankMethods(): Promise<BankMethodAdmin[]> {
  return apiFetch("/api/admin/bank-methods");
}
export async function adminSetBankActive(key: string, active: boolean): Promise<void> {
  await apiFetch(`/api/admin/bank-methods/${encodeURIComponent(key)}/active`,
    { method: "PUT", body: JSON.stringify({ active }) });
}
export async function adminSetBankOverride(key: string, ind: string, formula: string, enabled: boolean, label = ""): Promise<void> {
  await apiFetch(`/api/admin/bank-methods/${encodeURIComponent(key)}/indicators/${encodeURIComponent(ind)}/override`,
    { method: "PUT", body: JSON.stringify({ formula, enabled, label }) });
}
export async function adminDeleteBankOverride(key: string, ind: string): Promise<void> {
  await apiFetch(`/api/admin/bank-methods/${encodeURIComponent(key)}/indicators/${encodeURIComponent(ind)}/override`,
    { method: "DELETE" });
}

// ── Профили оценки (админ): подбор методики под банк для /evaluation ──────────
export interface EvaluationProfileCriteria {
  amount_min?: number | null; amount_max?: number | null;
  term_min_days?: number | null; term_max_days?: number | null;
  laws?: string[]; regions?: string[]; product_types?: string[];
}
export interface EvaluationProfile {
  id: number; bank_id: string; name: string; is_active: boolean; priority: number;
  criteria: EvaluationProfileCriteria; methodology_key: string;
  methodology_source: string; tariff_ref: string | null;
  created_at: string | null; updated_at: string | null;
}
export interface EvaluationProfileInput {
  bankId: string; name: string; methodologyKey: string;
  criteria?: EvaluationProfileCriteria; methodologySource?: string;
  tariffRef?: string | null; priority?: number; isActive?: boolean;
}

export async function adminListEvalProfiles(bankId?: string): Promise<EvaluationProfile[]> {
  const q = bankId ? `?bankId=${encodeURIComponent(bankId)}` : "";
  return apiFetch(`/api/admin/evaluation-profiles${q}`);
}
export async function adminCreateEvalProfile(payload: EvaluationProfileInput): Promise<EvaluationProfile> {
  return apiFetch("/api/admin/evaluation-profiles", { method: "POST", body: JSON.stringify(payload) });
}
export async function adminUpdateEvalProfile(id: number, payload: Partial<EvaluationProfileInput>): Promise<EvaluationProfile> {
  return apiFetch(`/api/admin/evaluation-profiles/${id}`, { method: "PUT", body: JSON.stringify(payload) });
}
export async function adminDeleteEvalProfile(id: number): Promise<void> {
  await apiFetch(`/api/admin/evaluation-profiles/${id}`, { method: "DELETE" });
}

// ── API-токены интеграционного сервиса (выдаёт админ) ────────────────────────
export interface ApiTokenRow {
  id: number;
  user_id: number;
  username: string;
  name: string;
  prefix: string;
  scopes: string;            // "read" / "read,ingest"
  is_active: boolean;
  created_at: string | null;
  expires_at: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
  sourcePriority: number | null;   // приоритет источника над Checko (для ingest)
  allowedSections: string[];       // разрешённые секции (пусто = все)
}

// Ответ на создание содержит сырой токен (`token`) — показывается один раз.
export interface ApiTokenCreated {
  id: number;
  user_id: number;
  name: string;
  prefix: string;
  scopes: string;
  is_active: boolean;
  created_at: string | null;
  expires_at: string | null;
  token: string;
}

export async function adminListApiTokens(): Promise<ApiTokenRow[]> {
  return apiFetch("/api/admin/api-tokens");
}

export async function adminCreateApiToken(payload: {
  user_id: number; name: string; scopes: string[]; expires_at?: string | null;
  priority?: number | null; allowed_sections?: string[];
}): Promise<ApiTokenCreated> {
  return apiFetch("/api/admin/api-tokens", { method: "POST", body: JSON.stringify(payload) });
}

export async function adminSetTokenSource(id: number, cfg: {
  priority?: number | null; allowed_sections?: string[]; is_active?: boolean;
}): Promise<any> {
  return apiFetch(`/api/admin/api-tokens/${id}/source`, { method: "PUT", body: JSON.stringify(cfg) });
}

export interface ApiTokenUsage {
  tokenId: number | null;
  requests: number;
  ok: number;
  clientErr: number;
  serverErr: number;
  distinctInn: number;
  lastAt: string | null;
}

export async function adminApiTokenUsage(dateFrom?: string, dateTo?: string): Promise<ApiTokenUsage[]> {
  const qs = new URLSearchParams();
  if (dateFrom) qs.set("date_from", dateFrom);
  if (dateTo) qs.set("date_to", dateTo);
  const q = qs.toString();
  return apiFetch(`/api/admin/api-tokens/usage${q ? "?" + q : ""}`);
}

export async function adminRevokeApiToken(id: number): Promise<void> {
  await apiFetch(`/api/admin/api-tokens/${id}/revoke`, { method: "POST" });
}

// ── Webhook-доставки (диагностика + dead-letter retry) ───────────────────────
export interface WebhookRow {
  id: number;
  token_id: number | null;
  url: string;
  event: string;
  status: string;            // pending | delivered | failed
  attempts: number;
  max_attempts: number;
  last_status: number | null;
  last_error: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export async function adminListWebhooks(status?: string, limit = 50): Promise<WebhookRow[]> {
  const qs = new URLSearchParams();
  if (status) qs.set("status", status);
  qs.set("limit", String(limit));
  return apiFetch(`/api/admin/webhooks?${qs.toString()}`);
}

export async function adminRetryWebhook(id: number): Promise<void> {
  await apiFetch(`/api/admin/webhooks/${id}/retry`, { method: "POST" });
}

export async function adminDeleteApiToken(id: number): Promise<void> {
  await apiFetch(`/api/admin/api-tokens/${id}`, { method: "DELETE" });
}

// ── Конструктор методик (премиум-владелец) ───────────────────────────────────

export interface VariablesResponse { variables: MethodologyVariable[]; functions: MethodologyFunction[] }

export async function fetchMethodologyVariables(): Promise<VariablesResponse> {
  return apiFetch<VariablesResponse>("/api/methodology/variables");
}

export async function fetchMethodologyTemplates(): Promise<MethodologyTemplate[]> {
  return apiFetch<MethodologyTemplate[]>("/api/methodology/templates");
}

// ── Поквартальная отчётность ─────────────────────────────────────────────────

export interface QuarterlyResponse { inn: string; data: Record<string, number>; count: number }
export interface ImportResponse {
  inn: string; imported: number; total: number; warnings: string[]; data: Record<string, number>;
  /** #575: скан/PDF ушёл в сервис распознавания — результата здесь ещё нет, см. `task`. */
  status?: "processing";
  task?: DocrecTask;
  message?: string;
}
/** Задача распознавания отчётности локальным сервисом ЛЛМ (#575). */
export interface DocrecTask {
  taskId: string;
  inn: string;
  fileName: string | null;
  reportKind: "annual" | "interim";
  status: "queued" | "done" | "error";
  error: string | null;
  errorCode: string | null;
  result: {
    inn?: string | null; organization?: string | null; reportDate?: string | null;
    reportKind?: string; codes?: number;
    periods?: { year: number; quarter: string | null; codes: number }[];
    saved?: { year: number; quarter: string | null; codes: number }[];
    /** Строки, которые сервис прислал, а мы не приняли, и почему (код / период / значение).
     *  Без этого потеря строки не видна: оператор смотрит на пустую клетку и не знает,
     *  сервис её не нашёл или мы отбросили. */
    rejected?: { code: string; name?: string | null; why: string }[];
    /** Задачу переотправили в очередь другого периода: сервис отбил документ как не тот
     *  вид отчётности. Вторая попытка удваивает ожидание — экран это объясняет. */
    retried?: boolean;
  } | null;
  createdAt: string | null;
  updatedAt: string | null;
}
export async function listDocrecTasks(inn: string): Promise<{
  inn: string; enabled: boolean; data: DocrecTask[];
  /** Сколько распознавание занимает обычно и до какого предела задача вообще живёт.
   *  Приходят с сервера — в вёрстке этим числам не место. */
  typicalSec?: number; timeoutMin?: number;
}> {
  return apiFetch(`/api/quarterly/${inn}/tasks`);
}

export async function getQuarterly(inn: string): Promise<QuarterlyResponse> {
  return apiFetch<QuarterlyResponse>(`/api/quarterly/${inn}`);
}

export async function saveQuarterly(inn: string, data: Record<string, number>): Promise<QuarterlyResponse> {
  return apiFetch<QuarterlyResponse>(`/api/quarterly/${inn}`, {
    method: "PUT", body: JSON.stringify({ data }),
  });
}

export async function importQuarterly(
  inn: string, file: File, quarter?: string, year?: number,
): Promise<ImportResponse> {
  // multipart — НЕ через apiFetch (он навязывает Content-Type: application/json).
  const fd = new FormData();
  fd.append("file", file);
  if (quarter) fd.append("quarter", quarter);
  if (year != null) fd.append("year", String(year));
  const token = getToken();
  const resp = await fetch(`/api/quarterly/${inn}/import`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  const body = await resp.json().catch(() => null);
  if (!resp.ok) {
    const detail = body && (body.detail || body.error);
    throw new Error(Array.isArray(detail) ? detail.map((d: any) => d.msg || d).join("; ") : (detail || `HTTP ${resp.status}`));
  }
  return body as ImportResponse;
}

export async function listMethodologies(): Promise<Methodology[]> {
  return apiFetch<Methodology[]>("/api/methodologies");
}

export async function getMethodology(id: number): Promise<Methodology> {
  return apiFetch<Methodology>(`/api/methodologies/${id}`);
}

export interface MethodologyPayload {
  name: string;
  key?: string;
  description?: string;
  definition: MethodologyDefinition;
  is_active: boolean;
}

export async function createMethodology(payload: MethodologyPayload): Promise<Methodology> {
  return apiFetch<Methodology>("/api/methodologies", {
    method: "POST", body: JSON.stringify(payload),
  });
}

export async function updateMethodology(id: number, payload: MethodologyPayload): Promise<Methodology> {
  return apiFetch<Methodology>(`/api/methodologies/${id}`, {
    method: "PUT", body: JSON.stringify(payload),
  });
}

export async function deleteMethodology(id: number): Promise<void> {
  await apiFetch(`/api/methodologies/${id}`, { method: "DELETE" });
}

export async function validateFormula(formula: string): Promise<{ ok: boolean; error: string }> {
  return apiFetch("/api/methodologies/validate", {
    method: "POST", body: JSON.stringify({ formula }),
  });
}

export interface PreviewResponse {
  ok: boolean;
  errors?: string[];
  company?: string;
  result?: CustomMethodologyResult;
}

export async function previewMethodology(
  definition: MethodologyDefinition, inn: string, desiredLimit?: number,
): Promise<PreviewResponse> {
  return apiFetch<PreviewResponse>("/api/methodologies/preview", {
    method: "POST", body: JSON.stringify({ definition, inn, desiredLimit }),
  });
}

// ── Раздел брокеров: справочники + реестр банков (CRUD — только админ) ────────
export async function fetchBrokerDict(): Promise<BrokerDict> {
  return apiFetch<BrokerDict>("/api/broker/dict");
}
export interface Okpd2Item { code: string; name: string; }
export async function searchOkpd2(q: string): Promise<Okpd2Item[]> {
  return apiFetch<Okpd2Item[]>(`/api/broker/okpd2?q=${encodeURIComponent(q)}`);
}
/** Отбор под сделку в подразделе «Тарифы»: площадки, банки, продукт, вид БГ, сумма гарантии. */
export interface TariffFilters {
  platforms?: string[]; banks?: string[]; product?: string; gtype?: string;
  // Сумма: точкой (amount) или диапазоном (amountFrom…amountTo).
  amount?: number | null; amountFrom?: number | null; amountTo?: number | null;
  compareDim?: string;
  // «Кто я»: подраздел смотрит сам банк или сама площадка, чтобы увидеть свою позицию.
  me?: string; meKind?: "банк" | "площадка";
}
/** Аналитика тарифов и КВ для подраздела «Тарифы» реестра банков. */
export async function tariffAnalytics(activeOnly = true, f: TariffFilters = {}): Promise<any> {
  const q = new URLSearchParams({ active_only: activeOnly ? "true" : "false" });
  if (f.platforms?.length) q.set("platform", f.platforms.join(","));
  if (f.banks?.length) q.set("banks", f.banks.join(","));
  if (f.product) q.set("product", f.product);
  if (f.gtype) q.set("gtype", f.gtype);
  if (f.amount) q.set("amount", String(f.amount));
  if (f.amountFrom) q.set("amount_from", String(f.amountFrom));
  if (f.amountTo) q.set("amount_to", String(f.amountTo));
  if (f.compareDim) q.set("compare_dim", f.compareDim);
  if (f.me) { q.set("me", f.me); q.set("me_kind", f.meKind || "банк"); }
  return apiFetch(`/api/broker/banks/tariff-analytics?${q.toString()}`);
}

export async function listBanks(): Promise<Bank[]> {
  return apiFetch<Bank[]>("/api/broker/banks");
}
/** Карточки для ЧТЕНИЯ: сравнение продуктов и аналитика. Оператору площадки приходит
 *  реестр целиком, остальным — их собственный скоуп (#595). */
export async function listMarketBanks(): Promise<Bank[]> {
  return apiFetch<Bank[]>("/api/broker/banks/market");
}
export async function createBank(payload: Bank): Promise<Bank> {
  return apiFetch<Bank>("/api/broker/banks", { method: "POST", body: JSON.stringify(payload) });
}
export async function updateBank(id: number, payload: Bank): Promise<Bank> {
  return apiFetch<Bank>(`/api/broker/banks/${id}`, { method: "PUT", body: JSON.stringify(payload) });
}
export async function deleteBank(id: number): Promise<void> {
  await apiFetch(`/api/broker/banks/${id}`, { method: "DELETE" });
}
export async function brokerCalc(req: Record<string, any>): Promise<BrokerCalcResult> {
  return apiFetch<BrokerCalcResult>("/api/broker/calc", { method: "POST", body: JSON.stringify(req) });
}

// Вероятные участники объявленного тендера (#957) — по кнопке, не часть brokerCalc.
export async function fetchLikelyParticipants(tenderNumber: string): Promise<LikelyParticipant[] | null> {
  const r = await apiFetch<{ participants: LikelyParticipant[] | null }>(
    `/api/broker/likely-participants?${new URLSearchParams({ tenderNumber })}`);
  return r.participants;
}

// Подходящие тендеры под клиента по ОКВЭД+региону+опыту (#958) — по кнопке. Сервер сам
// считает ОКВЭД/регион/опыт по ИНН (тот же профиль клиента, что у brokerCalc).
export async function fetchSuggestedTenders(inn: string): Promise<{ okved: string; tenders: SuggestedTender[] | null }> {
  return apiFetch(`/api/broker/suggested-tenders?${new URLSearchParams({ inn })}`);
}

// Данные тендера по реестровому номеру (из сервиса тендеров, НЕ из Checko) — для автозаполнения.
export interface BrokerTenderInfo {
  source?: string; purchaseNumber: string; law?: string | null; objectInfo?: string | null;
  sum?: number | null; currency?: string | null; customerInn?: string | null; customerName?: string | null;
  regionCode?: string | null; region?: string | null; okpd2?: string | null; okpd2Name?: string | null;
  applicationGuaranteeAmount?: number | null; contractGuaranteeAmount?: number | null;
  warrantyGuaranteeAmount?: number | null;
  applicationGuaranteePart?: number | null; contractGuaranteePart?: number | null;
  endDate?: string | null; publishedAt?: string | null;
  /** #556: БГ на гарантийные обязательства — сумма обеспечения ГО, срок гарантийных
   *  обязательств из текста извещения и посчитанные от них даты гарантии. null в поле —
   *  данных в извещении нет, срок называет брокер (см. notes). */
  warranty?: BrokerWarrantyPlan | null;
}
export interface BrokerWarrantyPlan {
  amount?: number | null; part?: number | null;
  termText?: string | null; termMonths?: number | null; extraMonths: number;
  worksEnd?: string | null; worksEndSource?: string | null;
  start?: string | null; end?: string | null; days?: number | null;
  procedureInfo?: string | null; notes: string[];
}
export async function brokerTenderLookup(number: string): Promise<BrokerTenderInfo> {
  return apiFetch<BrokerTenderInfo>(`/api/broker/tender/${encodeURIComponent(number)}`);
}

// Умный ассистент чата брокера: «неразобранное» обращение → LLM с tool-loop → ответ по данным ANYskin.
export interface AssistantToolCall { name: string; args: Record<string, any>; result: any; }
export interface AssistantReply { answer: string; toolCalls?: AssistantToolCall[]; rounds?: number; }
export async function brokerAssistant(message: string, context?: Record<string, any>,
                                      history?: { role: string; content: string }[],
                                      product: "offers" | "credit" | "apps" | "banks" | "analytics" | "clients" = "offers"): Promise<AssistantReply> {
  // `product` — подсказка маршрута (#611, #612). Наборы инструментов разнесены по функциям:
  // подбор, банки, кредиты, аналитика, заявки, клиенты — вместе они не помещаются в окно
  // модели. Сам вопрос маршрутизирует навигатор на сервере.
  return apiFetch<AssistantReply>("/api/broker/assistant", {
    method: "POST",
    body: JSON.stringify({ message, context: context || {}, history: history || [], product }),
  });
}
export async function brokerAssistantAccept(payload: {
  question: string; answer: string; context?: Record<string, any>;
  toolCalls?: AssistantToolCall[]; verdict?: "up" | "down"; tags?: string[];
}): Promise<{ status: string; id?: number; verdict?: string }> {
  return apiFetch("/api/broker/assistant/accept", { method: "POST", body: JSON.stringify(payload) });
}

// Детальный скоринг клиента под выбранный банк/методику (негативы/позитивы + риск-профиль).
export interface BrokerEvalItem { type: string; label?: string; block?: string; value?: number | null; score?: number | null; deduction?: number | null; passed?: boolean; }
export interface BrokerEvaluateResult {
  methodologyKey: string; bank?: string; product?: string;
  rating?: string; score?: number; scaleMax?: number;
  blocks?: any[]; stopFactors?: any[];
  risk?: { rating?: string; totalScore?: number; reliability?: string; creditRisk?: string; solvency?: string; recommendations?: string[] } | null;
  negatives?: BrokerEvalItem[]; positives?: BrokerEvalItem[];
  currentFinancials?: Record<string, number>;   // текущая отчётность клиента (для предзаполнения)
  projected?: boolean;                            // true — расчёт по своей отчётности
}
export async function brokerEvaluate(payload: {
  inn: string; methodologyKey: string; amount?: number;
  experienceDeclared?: boolean | null; overrides?: Record<string, any>;
  balance?: Record<string, number>;   // своя отчётность → пересчёт (проекция)
}): Promise<BrokerEvaluateResult> {
  return apiFetch<BrokerEvaluateResult>("/api/broker/evaluate", { method: "POST", body: JSON.stringify(payload) });
}

// Настройка анализа: опции для селекторов + сохраняемые профили.
export interface BrokerOptions {
  platforms: string[]; sources: string[];
  banks: Array<{ id: number; name: string }>;
  methodologies: Array<{ key: string; bank?: string; name?: string; amountMin?: number | null; amountMax?: number | null }>;
}
export async function brokerOptions(): Promise<BrokerOptions> {
  return apiFetch<BrokerOptions>("/api/broker/options");
}

export interface AnalysisProfileConfig {
  platforms?: string[]; sources?: string[]; bankIds?: number[]; methodologyKey?: string | null;
  defaultsOnly?: boolean;   // «Базовый» набор банков
}
export interface AnalysisProfile {
  id: number; name: string; config: AnalysisProfileConfig; isDefault: boolean;
  createdAt?: string; updatedAt?: string;
}
export async function listAnalysisProfiles(): Promise<AnalysisProfile[]> {
  return apiFetch<AnalysisProfile[]>("/api/broker/profiles");
}
export async function createAnalysisProfile(name: string, config: AnalysisProfileConfig, isDefault = false): Promise<AnalysisProfile> {
  return apiFetch<AnalysisProfile>("/api/broker/profiles", { method: "POST", body: JSON.stringify({ name, config, isDefault }) });
}
export async function updateAnalysisProfile(id: number, name: string, config: AnalysisProfileConfig, isDefault = false): Promise<AnalysisProfile> {
  return apiFetch<AnalysisProfile>(`/api/broker/profiles/${id}`, { method: "PUT", body: JSON.stringify({ name, config, isDefault }) });
}
export async function deleteAnalysisProfile(id: number): Promise<void> {
  await apiFetch(`/api/broker/profiles/${id}`, { method: "DELETE" });
}

// ── История подборов банков по клиенту (выбранные чекбоксом банки) ────────────
export type SelectionStatus = "scoring" | "analysis" | "result";
export interface SelectionIteration {
  no: number; at: string; event: string; count: number;
  by?: { id?: number | null; name?: string | null } | null;
  banks?: string[]; comment?: string | null;
}
export interface BrokerSelection {
  id: number; ref: string; seq: number; ownerId: number;
  inn?: string | null; companyName?: string | null;
  deal: Record<string, any>; selected: any[];
  status: SelectionStatus; origin: string; tokenId?: number | null;
  comment?: string | null; callbackUrl?: string | null;
  crmExportedAt?: string | null; crmRef?: string | null; createdAt?: string | null;
  assigneeId?: number | null;
  // Лог итераций подбора по кейсу: каждая отправка результата дописывает запись.
  iterations?: SelectionIteration[];
  sns?: { orderId: number; status?: string | null; submitted: boolean } | null;
  /** Результат скоринга принципала — отдаёт только GET /broker/selections/{ref} (#1046). */
  scoring?: BrokerScoringRow | null;
}

/** Одна карточка кейса по ref: кейс + поданная заявка + результат скоринга.
 *  Для виджета, который МПБ открывает сразу после скоринга — заявок тогда ещё нет,
 *  а список /broker/selections отдаёт только подборы текущего пользователя (#1046). */
export async function getBrokerSelection(ref: string): Promise<BrokerSelection> {
  return apiFetch<BrokerSelection>(`/api/broker/selections/${encodeURIComponent(ref)}`);
}
export async function saveBrokerSelection(payload: {
  seq?: number;   // задан → обновить существующий подбор (тот же ref), не создавать новый
  ref?: string | null;   // '{seq}-{ownerId}' — точный адрес кейса (задача из CRM принадлежит не мне)
  inn?: string | null; companyName?: string | null;
  deal: Record<string, any>; selected: any[]; status?: SelectionStatus;
  comment?: string | null;
  // Прогон подбора из ответа расчёта (#607): им в журнале выдачи отмечается, какие из
  // показанных офферов брокер выбрал. Без него Win Rate считается заниженным.
  runId?: string | null;
}): Promise<BrokerSelection> {
  return apiFetch<BrokerSelection>("/api/broker/selections", { method: "POST", body: JSON.stringify(payload) });
}
export async function listBrokerSelections(inn?: string, status?: string, limit = 50): Promise<BrokerSelection[]> {
  const q = new URLSearchParams();
  if (inn) q.set("inn", inn);
  if (status) q.set("status", status);
  q.set("limit", String(limit));
  return apiFetch<BrokerSelection[]>(`/api/broker/selections?${q.toString()}`);
}
// История скоринга принципалов в брокере (отдельный лог, /api/broker/scoring-history).
export interface BrokerScoringRow {
  id: number;
  inn: string;
  companyName?: string | null;
  rating?: string | null;
  reliability?: string | null;
  creditRisk?: string | null;
  scoringPassed?: boolean | null;
  stopFactors?: string[];
  date: string | null;
  /** #573: закупка, под которую считали скоринг — «Подобрать» возвращает к тем же торгам.
   *  У записей до 06.10.2026 номера нет: он тогда не сохранялся. */
  tenderNumber?: string | null;
}
export async function fetchBrokerScoringHistory(inn?: string, limit = 50): Promise<BrokerScoringRow[]> {
  const q = new URLSearchParams();
  if (inn) q.set("inn", inn);
  q.set("limit", String(limit));
  return apiFetch<BrokerScoringRow[]>(`/api/broker/scoring-history?${q.toString()}`);
}

// Задачи подбора (распределение на сотрудников): /api/broker/tasks.
export async function fetchBrokerTasks(scope: "mine" | "pool" | "org" = "mine", status?: string, limit = 100): Promise<BrokerSelection[]> {
  const q = new URLSearchParams();
  q.set("scope", scope);
  if (status) q.set("status", status);
  q.set("limit", String(limit));
  return apiFetch<BrokerSelection[]>(`/api/broker/tasks?${q.toString()}`);
}
export async function assignBrokerTask(id: number, employeeId: number | null): Promise<BrokerSelection> {
  return apiFetch<BrokerSelection>(`/api/broker/tasks/${id}/assign`, { method: "PUT", body: JSON.stringify({ employeeId }) });
}
export async function resolveBrokerTask(id: number, selected: any[], comment?: string): Promise<BrokerSelection> {
  return apiFetch<BrokerSelection>(`/api/broker/tasks/${id}/result`, { method: "PUT", body: JSON.stringify({ selected, comment }) });
}
export async function setOrgAutoDistribute(enabled: boolean): Promise<{ organization: any }> {
  return apiFetch<{ organization: any }>("/api/org/settings", { method: "PUT", body: JSON.stringify({ autoDistributeTasks: enabled }) });
}
// URL контрагента, куда POST-ить результат подбора (пустая строка — очистить).
export async function setOrgResultWebhook(url: string): Promise<{ organization: any }> {
  return apiFetch<{ organization: any }>("/api/org/settings", { method: "PUT", body: JSON.stringify({ resultWebhookUrl: url }) });
}

export async function setBrokerSelectionStatus(seq: number, status: SelectionStatus): Promise<BrokerSelection> {
  return apiFetch<BrokerSelection>(`/api/broker/selections/${seq}/status`, { method: "PUT", body: JSON.stringify({ status }) });
}
export async function crmExportSelection(seq: number, crmRef?: string, ref?: string | null): Promise<{ status: string; record: BrokerSelection; payload: any; webhook?: boolean }> {
  return apiFetch(`/api/broker/selections/${seq}/crm-export`, { method: "POST", body: JSON.stringify({ crmRef: crmRef || null, ref: ref || null }) });
}

// Проекция: пересчёт всех методик по переданному (целевому) балансу.
export async function calculateWithBalance(
  inn: string, desiredLimit: number, balance: Record<string, number>,
): Promise<AllMethodologiesResult> {
  return apiFetch<AllMethodologiesResult>("/api/v2/calculate-with-balance", {
    method: "POST", body: JSON.stringify({ inn, desiredLimit, balance }),
  });
}

// ── Опыт клиента (премиум/админ) ─────────────────────────────────────────────
export type RegistryContract = {
  regNumber: string; customer: string; subject: string; price: number;
  law: string; status: string; signDate: string; endDate: string; completed: boolean;
};

/** Госконтракты клиента, известные реестру, — рядом с ручными записями опыта.
 *  Читается из кэша источника: экран не тратит квоту запросов. */
export async function getClientContracts(inn: string): Promise<{
  inn: string; cached: boolean; items: RegistryContract[];
  totals: Record<string, number>; duplicateCount: number;
}> {
  return apiFetch(`/api/clients/${inn}/contracts`);
}

export type ReportingPeriod = {
  year: number; quarter: string | null; label: string;
  values: Record<string, number>; source: string; updatedAt: string | null;
};

/** Отчётность клиента по отчётным датам: годовые и квартальные периоды вместе.
 *  Хранилище одно — период отличает год от квартала, а не природа данных. */
export async function getClientReporting(inn: string): Promise<{
  inn: string; periods: ReportingPeriod[]; count: number;
}> {
  return apiFetch(`/api/clients/${inn}/reporting`);
}

/** Сохранить отчётную дату: ручной ввод — второй способ предоставить отчётность
 *  наряду с загрузкой файла. */
export async function saveClientReportingPeriod(
  inn: string, year: number, quarter: string | null, values: Record<string, number>) {
  const q = quarter ? `?quarter=${quarter}` : "";
  return apiFetch<{ inn: string; year: number; quarter: string | null; count: number }>(
    `/api/clients/${inn}/reporting/${year}${q}`,
    { method: "PUT", body: JSON.stringify({ values }) });
}

/** Реестр «Мои клиенты»: клиент, последний расчёт, отметки об отчётности и опыте.
 *  Область видимости — организация (для одиночной учётки — свои записи). */
export async function listClients(limit = 200): Promise<{
  rows: {
    inn: string; kind: "ul" | "ip"; name: string; ogrn: string; okved: string;
    status: string; region: string; mspCategory: string; registrationDate: string | null;
    lastCalculation: { rating: string | null; totalScore: number | null; date: string | null; by: string | null };
    financials: { periods: number; lastYear: number | null; hasQuarterly: boolean };
    experienceCount: number; selections: number;
  }[];
  scope: "org" | "user";
  /** Чьи подборы попали в счётчик: руководитель организации видит все, сотрудник — свои. */
  selectionsScope: "org" | "user";
}> {
  return apiFetch(`/api/clients?limit=${limit}`);
}

export async function getExperience(inn: string): Promise<{ inn: string; items: ExperienceItem[]; count: number }> {
  return apiFetch(`/api/experience/${inn}`);
}
export async function saveExperience(inn: string, items: ExperienceItem[]): Promise<{ inn: string; items: ExperienceItem[]; count: number }> {
  return apiFetch(`/api/experience/${inn}`, { method: "PUT", body: JSON.stringify({ items }) });
}

// Резолв связанных компаний по ОГРН — отдельный вызов по кнопке «связи».
// Возвращает связи 1-го уровня (с links2 — 2-й уровень) + сведения об усечении.
export async function resolveRelated(items: { ogrn: string; via: string }[]): Promise<{
  companies: RelatedCompanyShort[]; count: number;
  requested?: number; cap?: number; truncated?: boolean;
}> {
  return apiFetch("/api/related/resolve", { method: "POST", body: JSON.stringify({ items }) });
}

// ── Каталог банковских методик (оцифрованы из методик банков) ──────────────
export async function getBankCatalog(): Promise<BankCatalogItem[]> {
  return apiFetch("/api/methodology/bank-catalog");
}

export async function getBankSelection(): Promise<{ keys: string[] }> {
  return apiFetch("/api/methodology/bank-selection");
}

export async function setBankSelection(keys: string[]): Promise<{ keys: string[] }> {
  return apiFetch("/api/methodology/bank-selection", { method: "PUT", body: JSON.stringify({ keys }) });
}

// ── СНС: подача заявки в банк ────────────────────────────────────────────────
export interface SnsSubmissionState {
  data: Record<string, any> | null;
  ready: boolean;
  errors: string[];
  docTypes: Record<string, string>;
  docTypeMatch?: Record<string, string[]>;   // слова для распознавания типа по имени файла (#614)
  products?: Record<string, string>;              // productId → имя (единый источник — бэкенд)
  productByGuarantee?: Record<string, number>;    // guaranteeType → productId
  fromCard?: string[];                            // поля, подставленные из карточки клиента (#436)
  tender?: Record<string, any> | null;             // что уйдёт в СНС как fields.tender
  tenderMissing?: string[];                       // незаполненные обязательные поля fields.tender
  tenderLabels?: Record<string, string>;          // подписи полей fields.tender
  otherBankSelected?: boolean;                    // кроме Сбербанка выбран ещё банк — нужна полная бухотчётность
  needPurchase?: boolean;                         // тендера нет в базе или закупка закрытая: нужны основание и заказчик
  purchase?: { number?: string | null; found: boolean; law?: string | null; customerName?: string | null; customerInn?: string | null; guaranteeType?: string | null; category?: string | null };
  // Контракт: номер из заключённого контракта ЕИС (fromEis) и обязательность поля для продукта.
  contract?: { number?: string | null; signDate?: string | null; fromEis: boolean; required: boolean };
  warnings?: { docId: number; file: string; message: string }[];   // паспорт не того лица/организации
  // Что из документов у клиента уже лежит в СНС: она подтягивает их в новый заказ, поэтому
  // повторно просить не нужно (#613). Коды — наши doc_type.
  docTypesInSns?: string[];
  documentsInSns?: { typeId?: number; type?: string; docType?: string | null; meta?: any }[];
}

/** Документы клиента в СНС ДО сборки заявки (#613): искать файлы или СНС подставит сама.
 *  Ошибка и отсутствие токена — пустой состав, это справка, а не условие подачи. */
export async function snsClientDocuments(inn: string): Promise<{
  inn: string; docTypesInSns: string[];
  documentsInSns: { typeId?: number; type?: string; docType?: string | null; meta?: any }[];
  snsEnabled: boolean;
}> {
  return apiFetch(`/api/broker/sns/client-documents?inn=${encodeURIComponent(inn)}`);
}
export interface SnsDocMeta {
  id: number; file_name: string; doc_type?: string | null; mime?: string | null;
  size?: number | null; is_signature?: boolean; created_at?: string | null; expires_at?: string | null;
  meta?: { year?: number | null; quarter?: number | null } | null;
  /** Откуда взялся тип: manual — выбрал брокер, name — по имени файла,
   *  content — по содержимому первой страницы (#618). */
  type_source?: "manual" | "name" | "content" | null;
}
export interface SnsBankStatus {
  bankId: number; bankName?: string; status?: string; statusName?: string | null; cost?: number | null;
  number?: string | null; bankOrderId?: number;
  platform?: string | null;                         // площадка, через которую подключён банк (СНС-площадка по умолчанию)
  label?: string;                                   // решение банка по-русски (сервер)
  links?: { sign?: string; link?: string };         // ссылки, если СНС их прислала (в поле или в комментарии банка)
  note?: string | null;                             // комментарий банка, если СНС его прислала
}
/** Название решения банка для показа: русское от сервера, затем от банка, без сырого кода. */
export const bankStatusText = (b: SnsBankStatus) => b.label || b.statusName || "Статус банка обновлён";
// Допзапрос банка: что ANYskin ответил сам и что осталось брокеру.
export interface SnsApplicationRequest {
  id: number; bankId?: number | null; bankOrderId?: number | null; bankName?: string | null;
  status: string;                                   // OPEN | PROCESSING | RESOLVED | NEEDS_BROKER | FAILED
  proposal: { name: string; label: string; value: string }[];   // что ANYskin может ответить
  answered: string[];                               // что уже отправлено (подписи полей)
  missing: { name: string; label: string; editable: boolean; type?: string | null }[];
  error?: string | null; attempts?: number; updatedAt?: string | null;
}
export interface SnsSubmitResult {
  orderId: number; status: string; statusLabel?: string;
  inProgress?: boolean; blockers: any[]; banks?: SnsBankStatus[];
  queued?: boolean;                 // false — ждёт снятия блокеров и уйдёт в банк сама
  unavailableBanks?: number[];      // банки без тарифа под сумму/срок
  documentWarnings?: { type: string; files: string[]; message: string }[];
  applicationRequests?: SnsApplicationRequest[];
  signUrl?: string | null;          // ссылка на подписание клиентом из чата заявки (api-2)
}
// СНС принимает только эти форматы (контракт СНС) — для accept у выбора файла.
export const SNS_ACCEPT = ".pdf,.jpg,.jpeg,.png,.tif,.tiff,.sig,.p7s";
// Бухотчётность требует год и период: 1 — I кв., 2 — полугодие, 3 — 9 мес., 4 — год.
export const SNS_QUARTERS: Record<number, string> = { 1: "I квартал", 2: "полугодие", 3: "9 месяцев", 4: "год" };

export async function getSubmission(ref: string): Promise<SnsSubmissionState> {
  return apiFetch(`/api/broker/selections/${encodeURIComponent(ref)}/submission`);
}
export async function putSubmission(ref: string, fields: Record<string, any>): Promise<{ data: Record<string, any> }> {
  return apiFetch(`/api/broker/selections/${encodeURIComponent(ref)}/submission`, { method: "PUT", body: JSON.stringify(fields) });
}
export async function snsBanks(): Promise<{ id: number; name: string; platform?: string | null }[]> {
  const r = await apiFetch<{ data: { id: number; name: string; platform?: string | null }[] }>(`/api/broker/sns/banks`);
  return r.data;
}
// #439: токен СНС — настройка организации (сам токен наружу не отдаётся).
export interface OrgSnsState {
  configured: boolean; source: "org" | "global" | null;
  hint?: string | null; updatedAt?: string | null; updatedBy?: string | null; readable?: boolean;
  banksCount?: number;
}
export async function getOrgSns(): Promise<OrgSnsState> { return apiFetch("/api/org/integrations/sns"); }
export async function putOrgSns(token: string): Promise<OrgSnsState> {
  return apiFetch("/api/org/integrations/sns", { method: "PUT", body: JSON.stringify({ token }) });
}
export async function deleteOrgSns(): Promise<OrgSnsState> { return apiFetch("/api/org/integrations/sns", { method: "DELETE" }); }
// Вебхуки СНС: адрес для кабинета СНС («Вебхуки») + секрет подписи (HMAC).
export interface OrgSnsWebhookState {
  configured: boolean; path: string; events: string[];
  hint?: string | null; updatedAt?: string | null; updatedBy?: string | null;
  lastEventAt?: string | null; eventsCount?: number; autoResolve?: boolean;
  secret?: string;   // только в ответе на генерацию — показать один раз
}
export async function getOrgSnsWebhook(): Promise<OrgSnsWebhookState> { return apiFetch("/api/org/integrations/sns/webhook"); }
export async function putOrgSnsWebhook(secret?: string): Promise<OrgSnsWebhookState> {
  return apiFetch("/api/org/integrations/sns/webhook", { method: "PUT", body: JSON.stringify({ secret: secret || null }) });
}
export async function setOrgSnsAutoResolve(enabled: boolean): Promise<{ organization: any }> {
  return apiFetch<{ organization: any }>("/api/org/settings", { method: "PUT", body: JSON.stringify({ snsAutoResolve: enabled }) });
}
export async function resolveSnsRequest(ref: string, id: number, fields: Record<string, string>): Promise<{ data: SnsApplicationRequest }> {
  return apiFetch(`/api/broker/selections/${encodeURIComponent(ref)}/application-requests/${id}/resolve`,
    { method: "POST", body: JSON.stringify({ fields }) });
}
export async function deleteOrgSnsWebhook(): Promise<OrgSnsWebhookState> {
  return apiFetch("/api/org/integrations/sns/webhook", { method: "DELETE" });
}

// #436: подключён ли банк подбора к СНС (название + ручная привязка в реестре).
export interface SnsBankMatch {
  name: string; bankId?: number | null; connected: boolean;
  snsBankId: number | null; snsName: string | null; via: "manual" | "name" | null;
  // #445: канал СНС — площадка. platform — площадка решения; reason — почему не СНС:
  // other_platform (прямой канал) | no_sns_platform | manual_off | not_in_sns.
  platform?: string | null; reason?: string | null;
}
export async function snsResolve(banks: { bankId?: number | null; name: string; platform?: string }[]): Promise<SnsBankMatch[]> {
  const r = await apiFetch<{ data: SnsBankMatch[] }>(`/api/broker/sns/resolve`, { method: "POST", body: JSON.stringify({ banks }) });
  return r.data;
}

// #445: панель «Подключение к СНС» в реестре банков.
export interface SnsMappingItem { bankId: number; bank: string; platform: string; via?: "manual" | "name" | null }
export interface SnsMappingRow { snsBankId: number; snsName: string; matches: SnsMappingItem[]; excluded: SnsMappingItem[] }
export interface SnsMapping {
  snsPlatform: string; rows: SnsMappingRow[];
  options: { bankId: number; bank: string; platforms: string[] }[];
}
export async function getSnsMapping(): Promise<SnsMapping> { return apiFetch("/api/broker/sns/mapping"); }
export async function setSnsMapping(m: { bankId: number; platform: string; sns: boolean | null; snsBankId?: number }): Promise<void> {
  await apiFetch("/api/broker/sns/mapping", { method: "PUT", body: JSON.stringify(m) });
}

// ГИР БО (бухотчётность из ФНС): токен сервиса задаёт супер-админ в админке.
export interface GirboStatus {
  url: string; enabled: boolean; tokenSource: "env" | "admin" | null; envToken: boolean;
  stored: { hint: string; updatedAt: string | null; updatedBy: string | null; readable: boolean } | null;
  health?: { status?: string; version?: string; girbo_configured?: boolean; error?: string };
  years?: number[];
}
export async function getGirboStatus(): Promise<GirboStatus> { return apiFetch("/api/admin/integrations/girbo"); }
export async function setGirboToken(token: string): Promise<GirboStatus> {
  return apiFetch("/api/admin/integrations/girbo", { method: "PUT", body: JSON.stringify({ token }) });
}
export async function deleteGirboToken(): Promise<GirboStatus> {
  return apiFetch("/api/admin/integrations/girbo", { method: "DELETE" });
}

// Справочник реквизитов счёта по ОПФ: подстановка в заявку СНС по очереди, по кругу.
export interface AccountPoolItem {
  id: number; opf: string; opf_name: string; bik: string; bank_name: string;
  account: string; corr_account: string; position: number;
  is_active: boolean; issued_count: number; last_issued_at?: string | null;
}
export interface AccountPoolState {
  enabled: boolean; total: number; items: AccountPoolItem[];
  stats: { opf: string; total: number; active: number; issued: number }[];
}
export async function getAccountPool(params: { opf?: string; q?: string; limit?: number; offset?: number } = {}): Promise<AccountPoolState> {
  const qs = new URLSearchParams();
  if (params.opf) qs.set("opf", params.opf);
  if (params.q) qs.set("q", params.q);
  qs.set("limit", String(params.limit ?? 50));
  qs.set("offset", String(params.offset ?? 0));
  return apiFetch(`/api/admin/account-pool?${qs}`);
}
export async function setAccountPoolEnabled(enabled: boolean): Promise<{ enabled: boolean }> {
  return apiFetch("/api/admin/account-pool", { method: "PUT", body: JSON.stringify({ enabled }) });
}
export async function setAccountPoolItemActive(id: number, isActive: boolean): Promise<{ id: number; isActive: boolean }> {
  return apiFetch(`/api/admin/account-pool/${id}`, { method: "PUT", body: JSON.stringify({ isActive }) });
}

// #449: импорт условий банков из файла источника (ВБЦ …) в реестр.
export interface BankImportSource { source: string; enabled: boolean; note?: string; format?: string }
export interface BankImportItem {
  key: string; kind: "conditions" | "tariff" | "extra"; label: string | null; sheet?: string | null; row?: number | null;
  logo?: string | null; bank?: string | null;
  suggestion?: { bankId: number | null; via: "memory" | "logo" | "known_logo" | "platform" | "hint" | null };
  bankId: BankImportTarget; summary: string; warnings: string[]; raw?: string | null;
}
/** Банк реестра для строки файла: id, null — не импортировать, "new" — создать банк из файла. */
export type BankImportTarget = number | null | "new";
export interface BankImportChange { field: string; label: string; old: unknown; new: unknown }
export interface BankImportPlanBank {
  bankId: number | null; newBank?: string | null; bank: string; platformName: string;
  bankActive?: boolean; isNew?: boolean; reactivate?: boolean; skipped?: boolean;
  changes: BankImportChange[]; warnings: string[];
}
export interface BankImportPreview {
  id: number; source: string; fileName: string | null; appliedAt?: string | null; items: BankImportItem[];
  banks: { id: number; name: string; isActive: boolean }[];
  plan: { source: string; platform: string; banks: BankImportPlanBank[];
          missing: { bankId: number; bank: string; platform: string; deactivate: boolean }[]; notes: string[] };
  warnings: string[]; promo?: string | null; deactivateDefault?: boolean;
}
export interface BankImportResult {
  updated: string[]; created: string[]; deactivated: string[]; skipped: string[]; newBanks?: string[];
}
export async function getBankImportSources(): Promise<BankImportSource[]> { return apiFetch("/api/broker/banks/import/sources"); }
export async function uploadBankImport(source: string, file: File): Promise<BankImportPreview> {
  const fd = new FormData();
  fd.append("source", source);
  fd.append("file", file);
  const token = getToken();
  const resp = await fetch("/api/broker/banks/import", {
    method: "POST", headers: token ? { Authorization: `Bearer ${token}` } : {}, body: fd,
  });
  if (resp.status === 401) { clearSession(); throw new Error("Сессия истекла, войдите снова."); }
  const body = await resp.json().catch(() => null);
  if (!resp.ok) {
    const d = body && (body.detail || body.error);
    throw new Error(typeof d === "string" ? d : (d && d.message) || `HTTP ${resp.status}`);
  }
  return body;
}
export async function previewBankImport(id: number, mapping: Record<string, BankImportTarget>, deactivateMissing: boolean): Promise<BankImportPreview> {
  return apiFetch(`/api/broker/banks/import/${id}/preview`, { method: "POST", body: JSON.stringify({ mapping, deactivateMissing }) });
}
export async function applyBankImport(id: number, mapping: Record<string, BankImportTarget>, deactivateMissing: boolean): Promise<BankImportResult> {
  return apiFetch(`/api/broker/banks/import/${id}/apply`, { method: "POST", body: JSON.stringify({ mapping, deactivateMissing }) });
}

// #554: таблица условий реестра — выгрузка всех условий в xlsx, правка в Excel и загрузка обратно.
export interface BankSheetChange { path: string; label: string; from: unknown; to: unknown }
export interface BankSheetPlatform {
  key: string | null; row: number; platform: string | null; isNew: boolean; changes: BankSheetChange[];
}
export interface BankSheetBank {
  bankId: number | null; bank: string; isNew: boolean; changes: BankSheetChange[]; platforms: BankSheetPlatform[];
}
export interface BankSheetStats {
  rows: number; changedPlatforms: number; newPlatforms: number; newBanks: number; fields: number; clears: number;
}
export interface BankSheetPreview {
  id: number; fileName: string | null; banks: BankSheetBank[]; errors: string[];
  stats: BankSheetStats; newBanks: string[];
}
export interface BankSheetResult {
  updated: string[]; banksChanged: string[]; newBanks: string[]; stats: BankSheetStats; errors: string[];
}
/** Скачать книгу условий (xlsx). source — выгрузить только площадки одного источника. */
export async function downloadBankSheet(source?: string): Promise<void> {
  const token = getToken();
  const resp = await fetch("/api/broker/banks/sheet" + (source ? `?source=${encodeURIComponent(source)}` : ""),
    { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (resp.status === 401) { clearSession(); throw new Error("Сессия истекла, войдите снова."); }
  if (!resp.ok) throw new Error(`Не удалось выгрузить таблицу (HTTP ${resp.status})`);
  const link = document.createElement("a");
  link.href = URL.createObjectURL(await resp.blob());
  link.download = `anyskin-условия-${new Date().toISOString().slice(0, 10)}.xlsx`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 10_000);
}
export async function uploadBankSheet(file: File): Promise<BankSheetPreview> {
  const fd = new FormData();
  fd.append("file", file);
  const token = getToken();
  const resp = await fetch("/api/broker/banks/sheet", {
    method: "POST", headers: token ? { Authorization: `Bearer ${token}` } : {}, body: fd,
  });
  if (resp.status === 401) { clearSession(); throw new Error("Сессия истекла, войдите снова."); }
  const body = await resp.json().catch(() => null);
  if (!resp.ok) {
    const d = body && (body.detail || body.error);
    throw new Error(typeof d === "string" ? d : (d && d.message) || `HTTP ${resp.status}`);
  }
  return body;
}
export async function previewBankSheet(id: number): Promise<BankSheetPreview> {
  return apiFetch(`/api/broker/banks/sheet/${id}/preview`, { method: "POST", body: JSON.stringify({}) });
}
export async function applyBankSheet(id: number, force = false): Promise<BankSheetResult> {
  return apiFetch(`/api/broker/banks/sheet/${id}/apply`, { method: "POST", body: JSON.stringify({ force }) });
}

// #436: контакты и реквизиты клиента (карточка «Клиенты»).
export interface ClientContacts {
  inn?: string; contact_name?: string | null; contact_phone?: string | null; contact_email?: string | null;
  bank_bic?: string | null; payment_account?: string | null; correspondent_account?: string | null;
  updated_at?: string | null;
}
export async function getClientContacts(inn: string): Promise<ClientContacts | null> {
  const r = await apiFetch<{ data: ClientContacts | null }>(`/api/clients/${encodeURIComponent(inn)}/contacts`);
  return r.data;
}
export async function putClientContacts(inn: string, c: ClientContacts): Promise<ClientContacts | null> {
  const r = await apiFetch<{ data: ClientContacts | null }>(`/api/clients/${encodeURIComponent(inn)}/contacts`,
    { method: "PUT", body: JSON.stringify(c) });
  return r.data;
}

/** Поправить тип документа, который определился автоматически (#618). */
export async function setSubmissionDocType(docId: number, docType: string,
                                           meta?: { year?: number; quarter?: number }): Promise<any> {
  return apiFetch(`/api/broker/documents/${docId}`, {
    method: "PATCH",
    body: JSON.stringify({ doc_type: docType, year: meta?.year, quarter: meta?.quarter }),
  });
}

export async function listSubmissionDocs(ref: string): Promise<SnsDocMeta[]> {
  const r = await apiFetch<{ data: SnsDocMeta[] }>(`/api/broker/selections/${encodeURIComponent(ref)}/documents`);
  return r.data;
}
export async function uploadSubmissionDoc(ref: string, docType: string, file: File, isSignature = false,
                                          meta?: { year?: number; quarter?: number }): Promise<any> {
  const fd = new FormData();
  fd.append("files", file);
  fd.append("doc_type", docType);
  fd.append("is_signature", String(isSignature));
  if (meta?.year) fd.append("year", String(meta.year));
  if (meta?.quarter) fd.append("quarter", String(meta.quarter));
  const token = getToken();
  const resp = await fetch(`/api/broker/selections/${encodeURIComponent(ref)}/documents`, {
    method: "POST", headers: token ? { Authorization: `Bearer ${token}` } : {}, body: fd,
  });
  if (resp.status === 401) { clearSession(); throw new Error("Сессия истекла, войдите снова."); }
  const body = await resp.json().catch(() => null);
  if (!resp.ok) {
    const d = body && (body.detail || body.error);
    throw new Error(typeof d === "string" ? d : (d && d.message) || `HTTP ${resp.status}`);
  }
  return body;
}
export async function deleteSubmissionDoc(id: number): Promise<void> {
  await apiFetch(`/api/broker/documents/${id}`, { method: "DELETE" });
}
export async function submitToBank(ref: string): Promise<{ data: SnsSubmitResult }> {
  return apiFetch(`/api/broker/selections/${encodeURIComponent(ref)}/submit`, { method: "POST" });
}
// ── Раздел «Заявки»: отправленные в СНС заявки и лента событий по ним ────────
export interface SnsApplicationRow {
  selection_ref: string; org_id?: number | null; owner_id?: number | null; owner_name?: string | null;
  sns_order_id: number; sns_status?: string | null; submitted_at?: string | null; updated_at?: string | null;
  seen_at?: string | null; product_id?: number | null; inn?: string | null; company_name?: string | null;
  amount?: number | null; tender_number?: string | null; productName?: string | null; lawLabel?: string | null; banks: SnsBankStatus[];
  open_requests: number; unseen: number; unseen_attention: number; attention_banks: number;
  last_event?: { title: string; at: string; attention: boolean; tone?: EventTone } | null;
  /** Уровень «Банки» (A8, #1178): строка — пара «заявка + банк». Ключ строки свой,
   *  потому что одна заявка встречается в выдаче столько раз, со сколькими банками ушла. */
  row_id?: string; bank_id?: number | null; bank?: SnsBankStatus | null; bank_status?: string | null;
  /** Дельта опроса (A4, #1175): осталась ли изменившаяся строка в текущем отборе. */
  matches?: boolean;
}
/** Цвет уведомления: positive — зелёный (хорошая новость), attention — жёлтый (нужно действие), negative — красный. */
export type EventTone = "positive" | "attention" | "negative" | "neutral";
export interface SnsApplicationEvent {
  id: number; kind: "submitted" | "status" | "bank" | "request" | "answer" | string;
  title: string; detail?: any; attention: boolean; tone?: EventTone; created_at: string;
}
export interface SnsApplicationDetail extends SnsApplicationRow {
  statusLabel?: string | null; applicationRequests: SnsApplicationRequest[]; events: SnsApplicationEvent[];
  /** Ссылка на подписание клиентом — банк присылает её сообщением в чат заявки (api-2). */
  signUrl?: string | null;
}
/** Словарь статусов заказа и решений банков (A1, #1172): подписи, тон, группа фасета. */
export interface StatusMeta {
  code: string; label: string; short: string; tone: string;
  group: string; order: number; isFinal: boolean; hint?: string | null;
  /** Ранг продвижения сделки (A21, #1191): по нему канбан выбирает стадию заявки из
   *  статусов её банков. 0 — «это не продвижение» (отказ, отмена, архив). */
  progress?: number;
}
export interface StatusDict {
  order: StatusMeta[]; bank: StatusMeta[];
  /** Карта переноса фильтра между уровнями (A8, #1178). Ключ верхнего уровня — ОТКУДА:
   *  `orders` — код статуса заказа → коды статусов банка, `banks` — наоборот. */
  levelMap?: { orders: Record<string, string[]>; banks: Record<string, string[]> };
}
export async function getApplicationStatuses(): Promise<StatusDict> {
  return apiFetch(`/api/broker/applications/statuses`);
}
/** Запрос списка заявок: фильтры считает сервер (A2+A3, #1173/#1174). */
export interface ApplicationsQuery {
  scope?: "mine" | "org";
  group?: string;            // attention | active | closed | all
  status?: string[];         // коды статусов заказа
  bank?: number[];           // id банков
  q?: string;                // компания, ИНН, № заказа, реестровый номер, № в банке
  from?: string; to?: string;
  cursor?: string; limit?: number;
  /** Уровень выдачи (A8, #1178): заказы или срез по банкам. */
  level?: ApplicationsLevel;
}
export type ApplicationsLevel = "orders" | "banks";
export interface ApplicationsCounts {
  groups: Record<string, number>; statuses: Record<string, number>; banks: Record<string, number>;
}
export interface ApplicationsPage {
  data: SnsApplicationRow[];
  canSeeOrg: boolean;
  total?: number;
  nextCursor?: string | null;
  counts?: ApplicationsCounts;
  /** Версия выдачи (A4, #1175) — её же отдаём назад в `since` при опросе. */
  version?: string | null;
  level?: ApplicationsLevel;
}
/**
 * Ответ инкрементального опроса (A4, #1175). `kind: "same"` — сервер ответил `304` и
 * тела нет вовсе; `kind: "delta"` — пришли ТОЛЬКО изменившиеся строки, и у каждой
 * `matches` говорит, осталась ли она в текущем отборе. `reload` — дельтой расхождение
 * не описать, нужен полный запрос.
 */
export type ApplicationsDelta =
  | { kind: "same" }
  | { kind: "delta"; changed: SnsApplicationRow[]; reload: boolean; version?: string | null;
      total?: number; counts?: ApplicationsCounts; canSeeOrg: boolean };
/** «Что мешает» по заявке (A10, #1180): пробелы со степенью срочности и действием. */
export interface BlockerAction {
  type: "open-request" | "copy-link" | "open-sns" | "attach-docs";
  requestId?: number; url?: string; bankOrderId?: number | null; docType?: string | null;
}
export interface Blocker {
  id: string; kind: string; severity: "critical" | "high" | "normal";
  bankId?: number | null; bankName?: string; title: string; hint?: string;
  missing: { name: string; label?: string; type?: string | null }[];
  action: BlockerAction;
}
export interface BlockersView { items: Blocker[]; summary: Record<string, number> }
/** Справочник типов документов для ответа банку (A20, #1187). */
export interface DocTypeOption { code: string; label: string; periodRequired: boolean }
export interface DocTypesView {
  docTypes: DocTypeOption[];
  quarters: { value: number; label: string }[];
  allowedExtensions: string[];
  maxSizeMb: number;
}
export async function getSnsDocTypes(): Promise<{ data: DocTypesView }> {
  return apiFetch(`/api/broker/sns/doc-types`);
}

/**
 * Ответ банку одним действием: поля допзапроса и документы (A15/A20, #1185/#1187).
 *
 * Через XMLHttpRequest, а не fetch: прогресс ОТПРАВКИ fetch не отдаёт, а скан на 15 МБ
 * без полосы выглядит как зависший экран. Возвращает и статус — 207 означает, что часть
 * ушла, а часть нет, и об этом нужно сказать отдельно.
 */
export function bankReply(ref: string, form: FormData, onProgress?: (pct: number) => void):
    Promise<{ status: number; data: any }> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/broker/selections/${encodeURIComponent(ref)}/bank-reply`);
    const token = getToken();
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      let body: any = null;
      try { body = JSON.parse(xhr.responseText || "{}"); } catch { body = {}; }
      if (xhr.status >= 200 && xhr.status < 300) resolve({ status: xhr.status, data: body?.data ?? body });
      else reject(new Error(typeof body?.detail === "string" ? body.detail
        : body?.detail?.message || `Ошибка ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error("Нет связи с сервером — файлы не отправлены"));
    xhr.onabort = () => reject(new Error("Отправка отменена"));
    (bankReply as any)._last = xhr;
    xhr.send(form);
  });
}
/** Прервать текущую отправку (кнопка «Отменить»). */
export function abortBankReply() { (bankReply as any)._last?.abort(); }

export async function getApplicationBlockers(ref: string): Promise<{ data: BlockersView }> {
  return apiFetch(`/api/broker/applications/${encodeURIComponent(ref)}/blockers`);
}

export async function listApplications(query: ApplicationsQuery | "mine" | "org" = "mine"): Promise<ApplicationsPage> {
  const qy: ApplicationsQuery = typeof query === "string" ? { scope: query } : query;
  const p = new URLSearchParams();
  p.set("scope", qy.scope || "mine");
  if (qy.group && qy.group !== "all") p.set("group", qy.group);
  if (qy.status?.length) p.set("status", qy.status.join(","));
  if (qy.bank?.length) p.set("bank", qy.bank.join(","));
  if (qy.q?.trim()) p.set("q", qy.q.trim());
  if (qy.from) p.set("from", qy.from);
  if (qy.to) p.set("to", qy.to);
  if (qy.cursor) p.set("cursor", qy.cursor);
  if (qy.limit) p.set("limit", String(qy.limit));
  if (qy.level && qy.level !== "orders") p.set("level", qy.level);
  return apiFetch(`/api/broker/applications?${p.toString()}`);
}
/**
 * Инкрементальный опрос списка (A4, #1175): отдаём серверу версию, которую держим.
 *
 * Запрос идёт мимо `apiFetch` по двум причинам: `304` для него — ошибка (тела нет, а
 * `resp.ok` ложно), и нужен `cache: "no-store"` — иначе браузер может сам ответить на
 * `304` своей копией предыдущего тела, и дельта придёт дважды.
 */
export async function listApplicationsSince(
  query: ApplicationsQuery, since: string,
): Promise<ApplicationsDelta> {
  const p = new URLSearchParams();
  p.set("scope", query.scope || "mine");
  if (query.group && query.group !== "all") p.set("group", query.group);
  if (query.status?.length) p.set("status", query.status.join(","));
  if (query.bank?.length) p.set("bank", query.bank.join(","));
  if (query.q?.trim()) p.set("q", query.q.trim());
  if (query.from) p.set("from", query.from);
  if (query.to) p.set("to", query.to);
  if (query.level && query.level !== "orders") p.set("level", query.level);
  p.set("since", since);
  const headers = new Headers({ "X-Client": clientKind() });
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  let resp: Response;
  try {
    resp = await fetch(`/api/broker/applications?${p.toString()}`,
                       { headers, cache: "no-store" });
  } catch (e: any) {
    throw new Error("Сеть недоступна: " + (e?.message ?? e));
  }
  if (resp.status === 304) return { kind: "same" };
  if (resp.status === 401) { clearSession(); throw new Error("Сессия истекла, войдите снова."); }
  const body = await resp.json().catch(() => null);
  if (!resp.ok) throw new Error(body?.detail || `Ошибка ${resp.status}`);
  return { kind: "delta", changed: body?.changed || [], reload: !!body?.reload,
           version: body?.version, total: body?.total, counts: body?.counts,
           canSeeOrg: !!body?.canSeeOrg };
}
export async function getApplication(ref: string): Promise<{ data: SnsApplicationDetail }> {
  return apiFetch(`/api/broker/applications/${encodeURIComponent(ref)}`);
}
export async function markApplicationSeen(ref: string): Promise<void> {
  await apiFetch(`/api/broker/applications/${encodeURIComponent(ref)}/seen`, { method: "POST" });
}
export async function getApplicationsUnseen(): Promise<{ count: number; attention: number; lastId: number | null; lastTitle?: string | null; lastCompany?: string | null; lastTone?: EventTone }> {
  return apiFetch(`/api/broker/applications/unseen`);
}
/** Открыть раздел «Заявки» (опционально — на конкретной заявке) из любого места приложения. */
export function openApplication(ref?: string) {
  window.dispatchEvent(new CustomEvent("anyskin:open-application", { detail: { ref } }));
}

export async function getOrderStatus(ref: string, refresh = false): Promise<{ data: SnsSubmitResult }> {
  // По умолчанию — сохранённое состояние (его обновляют вебхуки и сверка); refresh=1 —
  // живой запрос в СНС: по кнопке «Обновить из СНС» и на фазе скоринга.
  return apiFetch(`/api/broker/selections/${encodeURIComponent(ref)}/order${refresh ? "?refresh=1" : ""}`,
                  { method: "GET" });
}

/** Диагностика заявки: что СНС реально прислала (банки со всеми полями и тела вебхуков). */
export interface SnsRawData {
  orderId: number; status?: string | null; banks: any[]; events: { type: string; receivedAt?: string | null; data: any }[];
  webhook?: { secretSet: boolean; eventsCount: number; lastEventAt?: string | null };   // приходят ли вообще вебхуки СНС
}
export async function getApplicationRaw(ref: string): Promise<{ data: SnsRawData }> {
  return apiFetch(`/api/broker/applications/${encodeURIComponent(ref)}/raw`);
}

/** Реквизиты заказчика по ИНН из Чекко (форма подачи для закупки без извещения в базе). */
export interface CustomerLookup { inn: string; name: string; kpp: string; ogrn: string; oktmo: string; address: string; phone: string; email: string }
export async function customerLookup(inn: string): Promise<CustomerLookup> {
  const r = await apiFetch<{ data: CustomerLookup }>(`/api/broker/customer-lookup/${encodeURIComponent(inn)}`);
  return r.data;
}

// ── Кредитные продукты (#211, docs/17): реестр, импорт из файла, подбор ──────
export interface CreditProduct {
  id: number;
  org_id: number | null;
  bank_id: number | null;
  partner_name: string;
  platform: string;
  source: string;
  category: string;
  /** Все виды продукта строки источника («КИК / ПОС / ВКЛ» — три вида), docs/17 §6.1. */
  categories: string[];
  product_name: string;
  credit_type: string;
  credit_types: string[];
  amount_min: number | null;
  amount_max: number | null;
  term_days_max: number | null;
  rate_min: number | null;
  terms: Record<string, any>;
  scoring: Record<string, any>;
  is_active: boolean;
  updated_at?: string | null;
}
export interface CreditOffer {
  id: number; partner: string; platform: string; category: string; categories: string[];
  product: string; creditType: string; creditTypes: string[]; amountMin: number | null; amountMax: number | null; termDaysMax: number | null;
  issueFeeRub: number | null; issueFeePct: number | null;
  agentKvRub: number | null; agentKvBase: "amount" | "bank_fee" | null;
  rateMin: number | null; rateText: string;
  eligible: boolean; reasons: string[]; advisories: { code: string; label: string }[];
  terms: Record<string, any>;
}

export async function listCreditProducts(category?: string, activeOnly = false):
    Promise<{ rows: CreditProduct[]; categories: string[] }> {
  const q = new URLSearchParams();
  if (category) q.set("category", category);
  if (activeOnly) q.set("active_only", "true");
  return apiFetch(`/api/broker/credit/products${q.toString() ? `?${q}` : ""}`);
}
export async function updateCreditProduct(id: number, patch: Partial<CreditProduct>): Promise<CreditProduct> {
  return apiFetch(`/api/broker/credit/products/${id}`, { method: "PUT", body: JSON.stringify(patch) });
}
export async function toggleCreditProduct(id: number, active: boolean): Promise<{ ok: boolean }> {
  return apiFetch(`/api/broker/credit/products/${id}/active`, { method: "POST", body: JSON.stringify({ active }) });
}
export async function deleteCreditProduct(id: number): Promise<{ ok: boolean }> {
  return apiFetch(`/api/broker/credit/products/${id}`, { method: "DELETE" });
}
export async function getCreditImportSources(): Promise<{ sources: string[]; formats: Record<string, string> }> {
  return apiFetch("/api/broker/credit/import/sources");
}
/** Импорт кредитов: предпросмотр (ничего не меняет) либо применение — один и тот же файл. */
async function creditImport(step: "preview" | "apply", source: string, file: File): Promise<any> {
  const fd = new FormData();
  fd.append("file", file);
  const token = getToken();
  const resp = await fetch(`/api/broker/credit/import/${step}?source=${encodeURIComponent(source)}`, {
    method: "POST", headers: token ? { Authorization: `Bearer ${token}` } : {}, body: fd,
  });
  if (resp.status === 401) { clearSession(); throw new Error("Сессия истекла, войдите снова."); }
  const body = await resp.json().catch(() => null);
  if (!resp.ok) throw new Error((body && (body.detail || body.error)) || `HTTP ${resp.status}`);
  return body;
}
export async function previewCreditImport(source: string, file: File):
    Promise<{ source: string; count: number; warnings: string[];
              items: { row: number; label: string; product: CreditProduct }[] }> {
  return creditImport("preview", source, file);
}
export async function applyCreditImport(source: string, file: File):
    Promise<{ source: string; saved: number; total: number; warnings: string[] }> {
  return creditImport("apply", source, file);
}
export interface CreditworthinessVerdict {
  methodology: string;
  riskCategory: string | null;
  fScore: number | null;
  coefficients: Record<string, { value?: number; group?: string; score?: number; label?: string }>;
  interpretation: string;
  /** Высокий риск — отказ по основной методике кредита; остальное решает банк. */
  passed: boolean;
}
export async function creditOffers(payload: {
  deal: Record<string, any>; inn?: string; principal?: Record<string, any>;
  variables?: Record<string, number>; category?: string; creditType?: string;
}): Promise<{
  count: number; eligibleCount: number; offers: CreditOffer[];
  principal?: Record<string, any>;
  risk?: Record<string, any> | null;
  /** Основная оценка для кредита — методика кредитоспособности (K1–K7, F-score). */
  creditworthiness?: CreditworthinessVerdict | null;
  clientAnalysis?: any;
}> {
  return apiFetch("/api/broker/credit/offers", { method: "POST", body: JSON.stringify(payload) });
}
export async function createCreditProduct(data: Partial<CreditProduct>): Promise<CreditProduct> {
  return apiFetch("/api/broker/credit/products", { method: "POST", body: JSON.stringify(data) });
}
export async function bulkToggleCreditProducts(ids: number[], active: boolean): Promise<{ changed: number }> {
  return apiFetch("/api/broker/credit/products/bulk-active", {
    method: "POST", body: JSON.stringify({ ids, active }),
  });
}

// ── Кабинет оператора площадки (#591) ───────────────────────────────────────
// Карточка банка общая для всех площадок, поэтому сервер отдаёт её без чужих записей:
// чужие тарифы и КВ — коммерческая тайна конкурентов.

export interface PlatformBranding {
  primary: string; primaryText: string; dark: string; surface: string;
  font: string; logoUrl: string; logoAlt: string;
}

export interface PlatformInfo {
  id: number; name: string; source: string | null; orgId: number | null;
  isActive: boolean; banks?: number;
  /** Фирменный стиль площадки: кабинет одевается в него (#599). Сервер отдаёт тему
   *  уже приведённой — значения безопасно подставлять в инлайновые стили. */
  branding?: PlatformBranding;
}

export interface PlatformBankCard {
  id: number; name: string; site?: string | null; rating?: string | null;
  config: { platforms: Record<string, any>[]; [k: string]: any };
  /** Сколько площадок у банка всего и сколько из них мои — без этого карточка выглядит
   *  так, будто банк работает только с нами. */
  platformsTotal: number; platformsMine: number;
}

export async function myPlatforms(): Promise<{ data: PlatformInfo[]; count: number; role: string | null; isOperator: boolean }> {
  return apiFetch("/api/platform/mine");
}

export async function platformBanks(platformId: number): Promise<{ data: PlatformBankCard[]; count: number }> {
  return apiFetch(`/api/platform/${platformId}/banks`);
}

/** Воронка сделок площадки (#608): этапы, конверсии, причины отказов, позиция по банкам.
 *  Срез меньше пяти подборов сервер не раскрывает — приходит `достаточно данных: false`. */
export async function platformFunnel(platformId: number, days = 30,
                                     f: { law?: string; gtype?: string } = {}): Promise<any> {
  const q = new URLSearchParams({ days: String(days) });
  if (f.law) q.set("law", f.law);
  if (f.gtype) q.set("gtype", f.gtype);
  return apiFetch(`/api/platform/${platformId}/funnel?${q.toString()}`);
}

/** ABC-анализ банков площадки (#605): группы по вкладу и ярлыки расхождений. */
export async function platformAbc(platformId: number, days = 30, measure = "объём",
                                  f: { law?: string; gtype?: string } = {}): Promise<any> {
  const q = new URLSearchParams({ days: String(days), measure });
  if (f.law) q.set("law", f.law);
  if (f.gtype) q.set("gtype", f.gtype);
  return apiFetch(`/api/platform/${platformId}/abc?${q.toString()}`);
}

/** Что требует внимания оператора площадки (#605). */
export async function platformAlerts(platformId: number, days = 30): Promise<any> {
  return apiFetch(`/api/platform/${platformId}/alerts?days=${days}`);
}

/** Скачать аналитику площадки одним CSV (#605).
 *
 *  Через fetch, а не обычной ссылкой: запрос требует Bearer-токена, которого у ссылки
 *  нет, — она вернулась бы 401 и браузер сохранил бы страницу ошибки. */
export async function downloadPlatformReport(platformId: number, days = 30): Promise<void> {
  const token = getToken();
  const resp = await fetch(`/api/platform/${platformId}/report.csv?days=${days}`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (resp.status === 401) { clearSession(); throw new Error("Сессия истекла, войдите снова."); }
  if (!resp.ok) throw new Error(`Не удалось выгрузить аналитику (HTTP ${resp.status})`);
  const link = document.createElement("a");
  link.href = URL.createObjectURL(await resp.blob());
  link.download = `anyskin-аналитика-${days}д-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 10_000);
}

export async function platformAvailableBanks(platformId: number): Promise<{ data: { id: number; name: string; rating?: string | null }[] }> {
  return apiFetch(`/api/platform/${platformId}/banks/available`);
}

export async function platformAddBank(platformId: number, bankId: number, patch: Record<string, any> = {}): Promise<PlatformBankCard> {
  return apiFetch(`/api/platform/${platformId}/banks/${bankId}`, { method: "POST", body: JSON.stringify({ patch }) });
}

export async function platformCreateBank(platformId: number, body: { name: string; site?: string; rating?: string; inn?: string; record?: Record<string, any> }): Promise<PlatformBankCard> {
  return apiFetch(`/api/platform/${platformId}/banks`, { method: "POST", body: JSON.stringify(body) });
}

export async function platformEditRecord(platformId: number, bankId: number, index: number, patch: Record<string, any>): Promise<PlatformBankCard> {
  return apiFetch(`/api/platform/${platformId}/banks/${bankId}/records/${index}`, { method: "PUT", body: JSON.stringify({ patch }) });
}

export async function platformRemoveRecord(platformId: number, bankId: number, index: number): Promise<{ ok: boolean }> {
  return apiFetch(`/api/platform/${platformId}/banks/${bankId}/records/${index}`, { method: "DELETE" });
}

// ── Админка площадок (#591): завести, назначить владельца, создать оператора ──
// Регистрации «самому себе» нет: площадка получает доступ к реестру банков, поэтому и
// площадку, и владельца, и учётные записи операторов заводит администратор.

export async function adminListPlatforms(): Promise<{ data: PlatformInfo[]; count: number }> {
  return apiFetch("/api/admin/platforms");
}

export async function adminCreatePlatform(body: { name: string; source?: string; org_id?: number | null }): Promise<PlatformInfo> {
  return apiFetch("/api/admin/platforms", { method: "POST", body: JSON.stringify(body) });
}

export async function setPlatformBranding(platformId: number, branding: Partial<PlatformBranding>): Promise<PlatformInfo> {
  return apiFetch(`/api/platform/${platformId}/branding`, { method: "PUT", body: JSON.stringify({ branding }) });
}

export async function adminSetPlatformOwner(platformId: number, orgId: number | null): Promise<PlatformInfo> {
  return apiFetch(`/api/admin/platforms/${platformId}/owner`, { method: "PUT", body: JSON.stringify({ org_id: orgId }) });
}

export async function adminCreatePlatformOperator(platformId: number, body: { username: string; email: string; password: string }): Promise<{ user: AdminUserRow; platform: PlatformInfo }> {
  return apiFetch(`/api/admin/platforms/${platformId}/operator`, { method: "POST", body: JSON.stringify(body) });
}
